import React, { useState, useMemo, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Typography,
  useMediaQuery,
} from "@mui/material";
import moment from "moment";
import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import EditIcon from "@mui/icons-material/Edit";

import BreadCrumb from "Components/Common/BreadCrumb";
import BaseTable from "Components/Common/BaseTable";
import FilterApplicationRequests from "../Common/FilterApplicationRequest";
import AccessDetailsApplication from "../Consumer/AccessDetails/ApplicantForm";

import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import { ACCESS_APPLICATION } from "tagTypes";
import { getOptionLabel } from "common/enums";
import Tooltip from "Components/Common/Tooltip";
import UtilityTunnelApplicationViewModal from "./UtilityTunnelApplicationViewModal";
import Modal from "Components/Common/Modal";
import { useSelector } from "react-redux";
import { getImageUrl, isAuthorizedUser, onlyNumberKeys } from "utils";
import {
  UTA_CARD_ISSUANCE_INCHARGE,
  utilityTunnelConsumerRoles,
} from "common/constant";
import PreviewIcon from "@mui/icons-material/Preview";
import { useFormik } from "formik";
import * as Yup from "yup";
import InputFieldText from "Components/Common/InputFieldText";

import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DocumentPreviewModal from "Components/Common/DocumentPreviewModal";
import { usePostAccessCardDetail } from "apis/UtilityTunnel/AccessCardDetails";

const RECORDS_PER_PAGE = 10;

const UtilityTunnelApplicationRequestList = ({ t }) => {
  const [registerAccessDetail, { isLoading }] = usePostAccessCardDetail();

  const [query, setQuery] = useState({});
  const { user } = useSelector((state) => state.Login);

  const lg = useMediaQuery("(min-width: 768px) and (max-width: 1399px)");
  const md = useMediaQuery("(min-width: 576px) and (max-width: 767px)");
  const sm = useMediaQuery("(max-width: 575px)");

  const [editApplicationId, setEditApplicationId] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [isResetPageRegistered, setIsResetPageRegistered] = useState(false);
  const [applicationIdForIssueCardModal, setApplicationIdForIssueCardModal] =
    useState(null);

  const [documentToPreview, setDocumentToPreview] = useState();

  const resetPageRef = useRef();

  const viewApplicationId = searchParams.get("applicationId");

  const handleResetPage = useCallback(resetPageRef.current, [
    isResetPageRegistered,
  ]);

  const { roles } = useSelector((state) => state.Login);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: { accessCardNumber: "" },
    validationSchema: Yup.object({
      accessCardNumber: Yup.string().required("Access card number is required"),
    }),
    onSubmit: async (values) => {
      try {
        await registerAccessDetail({
          accessDetailId: applicationIdForIssueCardModal,
          accessCardNumber: values.accessCardNumber,
        });
        formik.resetForm();
        setApplicationIdForIssueCardModal(null);
      } catch (error) {}
    },
  });

  const isConsumer = useMemo(
    () => isAuthorizedUser(roles, utilityTunnelConsumerRoles),
    [roles]
  );

  const columns = [
    { title: "Access Card Number", field: "accessCardNumber" },
    {
      title: "Card Holder",
      renderCell: ({ accessCardHolderType }) =>
        getOptionLabel("utilityTunnelConsumerType", accessCardHolderType) ??
        "-",
    },
    {
      title: "Name",
      renderCell: ({ employeeContractorDetail = {} }) =>
        employeeContractorDetail?.firstName &&
        employeeContractorDetail?.firstName
          ? `${employeeContractorDetail.firstName} ${employeeContractorDetail.lastName}`
          : "--",
    },
    {
      title: "Email",
      field: "employeeContractorDetail.email",
    },
    {
      title: "Mobile Number",
      field: "employeeContractorDetail.mobileNumber",
    },
    {
      title: "Date",
      renderCell: ({ createdAt }) =>
        moment(createdAt).isValid()
          ? convertUTCDateToLocalDate(createdAt).format("DD-MM-YYYY")
          : "N/A",
    },
    {
      title: "Status",
      field: "utaCardIssuanceStatus.name",
    },
    {
      title: "Actions",
      renderCell: (item) => {
        const { utaCardIssuanceStatus, id, accessCardNumber, formUrl } = item;
        const allowedStatusForEdit = ["Submitted"];
        const isEditEnable = allowedStatusForEdit.includes(
          utaCardIssuanceStatus?.name
        );

        const isIssueCardVisible =
          isAuthorizedUser(roles, [UTA_CARD_ISSUANCE_INCHARGE]) &&
          ["Approved"].includes(utaCardIssuanceStatus?.name) &&
          !accessCardNumber;

        return (
          <Box width="150px">
            <IconButton
              onClick={() => {
                searchParams.set("applicationId", id);
                setSearchParams(searchParams);
              }}
            >
              <Tooltip title={t("View Application")}>
                <RemoveRedEyeIcon
                  fontSize="small"
                  color="primary"
                  className="cursor-pointer"
                />
              </Tooltip>
            </IconButton>

            {/* Edit */}

            {isConsumer && (
              <IconButton
                disabled={!isEditEnable}
                onClick={() => {
                  setEditApplicationId(id);
                }}
              >
                <Tooltip title={t("Edit Application Form")}>
                  <EditIcon
                    fontSize="small"
                    className="cursor-pointer"
                    color={isEditEnable ? "primary" : "disabled"}
                  />
                </Tooltip>
              </IconButton>
            )}

            {isIssueCardVisible && (
              <IconButton onClick={() => setApplicationIdForIssueCardModal(id)}>
                <Tooltip title={t("Issue card number")}>
                  <CheckCircleIcon
                    fontSize="small"
                    className="cursor-pointer"
                    color="success"
                  />
                </Tooltip>
              </IconButton>
            )}

            {!!formUrl && (
              <Tooltip title={t("Access Card")}>
                <IconButton
                  onClick={() => {
                    setDocumentToPreview({
                      url: getImageUrl(formUrl),
                      documentTypeName: "Access Card",
                      documentType: "pdf",
                      documentName: "AccessCard",
                    });
                  }}
                  size="small"
                >
                  <PreviewIcon color="primary" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ];

  const serverSidePaginationConfig = useMemo(
    () => ({
      endpoint: "access-detail",
      tags: [ACCESS_APPLICATION],
      query,
    }),
    [query]
  );

  return (
    <>
      {!!applicationIdForIssueCardModal && (
        <Modal
          isOpen
          handleClose={() => {
            formik.resetForm();
            setApplicationIdForIssueCardModal(null);
          }}
          title="Access Card Number"
          maxWidth="sm"
        >
          <div className="mt-3">
            <InputFieldText
              label={t("Access Card Number") + "*"}
              name="accessCardNumber"
              formik={formik}
              type="text"
              placeholder="Enter Access Card Number"
            />

            <Button
              onClick={formik.handleSubmit}
              variant="contained"
              className="d-flex ms-auto gap-2"
              disabled={isLoading}
            >
              {t("Submit")}
              {isLoading && <CircularProgress size={20} />}
            </Button>
          </div>
        </Modal>
      )}

      {!!documentToPreview && (
        <DocumentPreviewModal
          isOpen
          handleClose={() => {
            setDocumentToPreview(null);
          }}
          {...documentToPreview}
        />
      )}

      {!!editApplicationId && (
        <Modal
          isOpen={!!editApplicationId}
          handleClose={() => setEditApplicationId("")}
          maxWidth="lg"
          title="Edit Access Application Form"
        >
          <AccessDetailsApplication
            handleClose={() => setEditApplicationId("")}
            editApplicationId={editApplicationId}
          />
        </Modal>
      )}

      {!!viewApplicationId && (
        <UtilityTunnelApplicationViewModal
          applicationId={viewApplicationId}
          handleClose={() => {
            searchParams.delete("applicationId");
            setSearchParams(searchParams);
          }}
        />
      )}

      <div>
        {(!user?.signatureImage || !user?.profileImage) && isConsumer && (
          <Typography color={"error"}>
            * Please upload your profile image and signature to proceed with
            application.
          </Typography>
        )}

        <BreadCrumb title={t("Application Request Details")} />
        <FilterApplicationRequests
          setQuery={setQuery}
          resetPage={handleResetPage}
        />

        <BaseTable
          columns={columns}
          pageSize={RECORDS_PER_PAGE}
          paginationType="serverSide"
          serverSidePaginationConfig={serverSidePaginationConfig}
          registerResetPage={(cb) => {
            resetPageRef.current = cb;
            setIsResetPageRegistered((prev) => !prev);
          }}
          maxHeight={
            sm
              ? "calc(100vh - 400px)"
              : md
              ? "calc(100vh - 360px)"
              : lg
              ? "calc(100vh - 320px)"
              : "calc(100vh - 270px)"
          }
        />
      </div>
    </>
  );
};

UtilityTunnelApplicationRequestList.propTypes = {
  t: PropTypes.func,
};

export default withTranslation()(UtilityTunnelApplicationRequestList);
